"use client";

import { useMemo, useRef } from "react";
import { ImageRenderer } from "./ImageRenderer";
import { ImageContext } from "./imageSizing";
import KeywordTooltip, { useTooltipStyles } from "./KeywordTooltip";
import { tokensToHtml, tokensToHtmlWithKeywords } from "./tokenizer";
import { ContentToken } from "./types";

type KeywordToken = Extract<ContentToken, { type: "keyword" }>;

type Segment =
  | { kind: "html"; html: string }
  | { kind: "image"; token: Extract<ContentToken, { type: "image" }> }
  | { kind: "keywords"; html: string; keywords: KeywordToken[] };

/** One ref + tooltip per keywords flush so data-kw-idx lines up when images split content. */
function KeywordsSegment({
  html,
  keywords,
}: {
  html: string;
  keywords: KeywordToken[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  return (
    <>
      <div
        ref={containerRef}
        className="content-token"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <KeywordTooltip
        containerRef={containerRef}
        keywords={keywords}
        renderDefinition={(definition) => (
          <ContentRenderer content={definition} />
        )}
      />
    </>
  );
}

export function ContentRenderer({
  content,
  imageContext,
}: {
  content: ContentToken[] | string;
  imageContext?: ImageContext;
}) {
  const { classes } = useTooltipStyles();

  const segments = useMemo((): Segment[] => {
    if (typeof content === "string") {
      return [{ kind: "html", html: content }];
    }

    const result: Segment[] = [];
    let buf: ContentToken[] = [];

    const flushBuf = () => {
      if (buf.length === 0) return;
      const hasKw = buf.some((t) => t.type === "keyword");
      if (hasKw) {
        const { html, keywords } = tokensToHtmlWithKeywords(
          buf,
          classes.term,
          imageContext
        );
        result.push({ kind: "keywords", html, keywords });
      } else {
        result.push({ kind: "html", html: tokensToHtml(buf, imageContext) });
      }
      buf = [];
    };

    for (const t of content) {
      if (t.type === "image" && !t.nested) {
        flushBuf();
        result.push({ kind: "image", token: t });
      } else {
        buf.push(t);
      }
    }
    flushBuf();

    return result;
  }, [content, classes.term, imageContext]);

  return (
    <>
      {segments.map((seg, i) => {
        switch (seg.kind) {
          case "html":
            return (
              <div
                key={i}
                className="content-token"
                dangerouslySetInnerHTML={{ __html: seg.html }}
              />
            );
          case "image":
            return (
              <ImageRenderer
                key={i}
                src={seg.token.src}
                alt={seg.token.alt}
                width={seg.token.width}
                height={seg.token.height}
                displayMaxWidth={seg.token.displayMaxWidth}
                type={imageContext}
              />
            );
          case "keywords":
            return (
              <KeywordsSegment
                key={i}
                html={seg.html}
                keywords={seg.keywords}
              />
            );
        }
      })}
    </>
  );
}
